import React, { useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import './Pagination.scss';

interface PaginationProps {
    currentPage: number;
    perPage: number;
    pagination?: {
        currentPage: number;
        lastPage: number;
        perPage: number;
        total: number;
    };
    onPageChange: (page: number) => void;
    onPerPageChange: (perPage: number) => void;
    label?: string;
}

const PaginationComponent: React.FC<PaginationProps> = ({
    currentPage,
    perPage,
    pagination,
    onPageChange,
    onPerPageChange,
    label
}) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    // Inside the component so the "All" label re-translates on locale change.
    // Previously at module scope, which captured locale at first import only.
    const perPageOptions = useMemo(() => [
        { value: 20, label: '20' },
        { value: 50, label: '50' },
        { value: 100, label: '100' },
        { value: 250, label: '250' },
        { value: 500, label: '500' },
        { value: 0, label: fm('common.pagination.all', 'All') },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    // The default row-name label is opaque to this component — caller-provided.
    // Fall back to "records" if none given so the sentence still reads cleanly.
    const resolvedLabel = label ?? fm('common.export.records', 'records');

    const [ppOpen, setPpOpen] = useState(false);

    if (!pagination) return null;

    const { lastPage, total } = pagination;
    const showingAll = perPage === 0;
    const maxVisible = 5;

    // Page range calculation
    const pages: number[] = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(lastPage, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
        startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    const from = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
    const to = Math.min(currentPage * perPage, total);

    const allLabel = fm('common.pagination.all', 'All');

    return (
        <div className="mika-pagination">
            <div className="mika-pagination-info">
                <span className="mika-pagination-text">
                    {showingAll
                        ? `${allLabel} ${total} ${resolvedLabel}`
                        : total === 0
                            ? `0 ${resolvedLabel}`
                            : <>{from}&ndash;{to} <span className="mika-pagination-of"><FormattedMessage id="common.pagination.of" defaultMessage="of" /></span> {total} {resolvedLabel}</>
                    }
                </span>
                <div className="mika-pp-dropdown" tabIndex={0} onBlur={() => setTimeout(() => setPpOpen(false), 150)}>
                    <button className="mika-pagination-select" onClick={() => setPpOpen(!ppOpen)} type="button">
                        {perPageOptions.find(o => o.value === perPage)?.label || perPage}
                    </button>
                    {ppOpen && (
                        <div className="mika-pp-menu">
                            {perPageOptions.map(opt => (
                                <button
                                    key={opt.value}
                                    className={`mika-pp-option ${perPage === opt.value ? 'mika-pp-option-active' : ''}`}
                                    onClick={() => { onPerPageChange(opt.value); setPpOpen(false); }}
                                    type="button"
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <span className="mika-pagination-perpage-label">
                    <FormattedMessage id="common.pagination.perPage" defaultMessage="per page" />
                </span>
            </div>

            {!showingAll && lastPage > 1 && (
                <div className="mika-pagination-nav">
                    <button
                        className="mika-page-btn mika-page-arrow"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>

                    {startPage > 1 && (
                        <>
                            <button className="mika-page-btn" onClick={() => onPageChange(1)}>1</button>
                            {startPage > 2 && <span className="mika-page-ellipsis">&hellip;</span>}
                        </>
                    )}

                    {pages.map(p => (
                        <button
                            key={p}
                            className={`mika-page-btn ${p === currentPage ? 'mika-page-active' : ''}`}
                            onClick={() => onPageChange(p)}
                        >
                            {p}
                        </button>
                    ))}

                    {endPage < lastPage && (
                        <>
                            {endPage < lastPage - 1 && <span className="mika-page-ellipsis">&hellip;</span>}
                            <button className="mika-page-btn" onClick={() => onPageChange(lastPage)}>{lastPage}</button>
                        </>
                    )}

                    <button
                        className="mika-page-btn mika-page-arrow"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === lastPage}
                        aria-label="Next"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default PaginationComponent;
